/**
 * Local light/dark/system theme toggle. No next-themes: this is the same
 * ~20-line shape as tokens.ts's currentBrand/applyBrand pair (read/write an
 * attribute — here a class — on the root element), and avoids pulling in a
 * dependency plus its SSR-hydration ceremony for a single class toggle.
 */

export type ThemeMode = "system" | "light" | "dark";

export const THEME_MODES: readonly ThemeMode[] = ["system", "light", "dark"];

const STORAGE_KEY = "zw-theme-mode";

/** Resolve a mode to the concrete theme actually rendered. */
export function resolveTheme(mode: ThemeMode): "light" | "dark" {
  if (mode !== "system") return mode;
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/** Read the persisted mode, defaulting to "light" (SSR-safe).
 *
 * "light" — not "system" — is the reference VIP app's own pinned default
 * (its ThemeProvider sets defaultTheme="light"), and cutover invisibility
 * requires the served VIP experience to boot the way VIP prod does on any
 * machine. ZW is unaffected: its brand declares ONE palette at the
 * variable layer, so the unstored default changes nothing it renders. */
export function readStoredThemeMode(): ThemeMode {
  if (typeof window === "undefined") return "light";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === "light" || stored === "dark" || stored === "system" ? stored : "light";
}

/** Apply a mode: toggle the .dark class globals.css expects, and persist it. */
export function applyThemeMode(root: Element, mode: ThemeMode): void {
  root.classList.toggle("dark", resolveTheme(mode) === "dark");
  if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, mode);
}
